import { Component, OnInit, OnDestroy, Type, Injector, InjectionToken, ChangeDetectorRef } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, AuthError, AuthenticationResult } from '@azure/msal-browser';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { UserService } from './services/user.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LoaderInterceptor } from './core/interceptor/loader.interceptor';
import { UserRoleService } from './core/services/role.service';
import { ColorService } from './services/color.service';
import { LogoService } from './services/logo.service';
import { Subject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DrawerConfig, DrawerService } from './core/services/drawer.service';
import { WebBridgeService } from './core/services/web-bridge.service';
import { AnalyticsService } from './analytics/analytics.service';
import { SentryContextService } from './core/sentry/sentry-context.service';
import { SessionActivityService } from './core/services/session-activity.service';
import { LocalStorageService } from './services/LocalStorage.service';
import { AuthService } from './auth/auth.service';
import { LoaderService } from './core/services/loader.service';
import { ROUTE_PATHS } from './core/constants/route.constants';
import { SnackbarService } from './shared/snackbar/snackbar.service';
import { ACCESS_DENIED_NOTICE_KEY } from './core/constants/access-denied.constants';
import { UserLocaleService } from './services/user-locale.service';

export const DRAWER_DATA = new InjectionToken<any>('DRAWER_DATA');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'cafler-frontend';
  checkIsCollapsed = false;
  userRole: string = '';
  private destroy$ = new Subject<void>();
  private msalEventCallbackId: string | null = null;
  isDrawerOpen$: Observable<boolean>;
  currentDrawerComponentType: Type<any> | null = null;
  currentDrawerInjector: Injector | undefined = undefined;
  currentDrawerWidth: string = '400px';
  hideChrome: boolean = window.location.pathname.startsWith('/cta/');

  /** True when the viewport is mobile-sized (<600px). Chat-only mode. */
  isMobileChatMode = false;
  private mobileQuery?: MediaQueryList;
  private mobileQueryListener?: () => void;

  public drawerDataToken = DRAWER_DATA;

  constructor(
    private msalService: MsalService,
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
    private msalBroadcastService: MsalBroadcastService,
    private loaderInterceptor: LoaderInterceptor,
    private userRoleService: UserRoleService,
    private colorService: ColorService,
    private logoService: LogoService,
    private translate: TranslateService,
    public drawerService: DrawerService,
    private injector: Injector,
    private cdRef: ChangeDetectorRef,
    private webBridge: WebBridgeService,
    private sentryContextService: SentryContextService,
    private sessionActivityService: SessionActivityService,
    private localStorageService: LocalStorageService,
    private loaderService: LoaderService,
    private snackbarService: SnackbarService,
    private _userLocaleService: UserLocaleService,
    private analytics: AnalyticsService,
    private activatedRoute: ActivatedRoute
  ) {

    this.isDrawerOpen$ = this.drawerService.isOpen$;
  }

  ngOnInit() {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) route = route.firstChild;
        return route.snapshot.data;
      }),
      takeUntil(this.destroy$)
    ).subscribe((data) => {
      this.hideChrome = !!data['hideChrome'];
    });

    // Mobile: block navigation away from chat
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: any) => {
      if (this.isMobileChatMode && !event.urlAfterRedirects?.startsWith('/chat-ai')) {
        this.router.navigate(['/chat-ai']);
      }
    });

    this.sentryContextService.init();
    this.sessionActivityService.start();
    this.showAccessDeniedNotice();

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdRef.detectChanges();
      });

    // Mobile chat-only mode: detect viewport and redirect to chat
    this.mobileQuery = window.matchMedia('(max-width: 600px)');
    this.isMobileChatMode = this.mobileQuery.matches;
    this.mobileQueryListener = () => {
      this.isMobileChatMode = this.mobileQuery!.matches;
      if (this.isMobileChatMode) {
        document.body.classList.add('mobile-chat-only');
      } else {
        document.body.classList.remove('mobile-chat-only');
      }
    };
    this.mobileQuery.addEventListener('change', this.mobileQueryListener);
    if (this.isMobileChatMode) {
      document.body.classList.add('mobile-chat-only');
    }

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          if (this.msalService.instance.getAllAccounts().length > 0) {
            this.userService.refreshUserInfo();

            this.userRoleService.getCurrentUser().subscribe({
              next: (user) => {
                if (user && user.roles && user.roles.length > 0) {
                  this.userRole = user.roles[0].toLowerCase();
                }
              },
              error: (error) => console.error('Error loading user in app component:', error)
            });

            this.userRoleService.getCenterHue()
              .pipe(takeUntil(this.destroy$))
              .subscribe(hue => {
                if (hue !== null) {
                  this.colorService.setPrimaryHue(hue);
                } else {
                  this.colorService.setPrimaryHue(145);
                }
              });

            this.userRoleService.getCenterLogo()
              .pipe(takeUntil(this.destroy$))
              .subscribe(logo => {
                this.logoService.setLogoUrl(logo);
              });
          }
        },
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
            msg.eventType === EventType.LOGOUT_SUCCESS ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (msg) => {
          if (msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
            const payload = msg.payload as AuthenticationResult | null;
            if (payload?.account) {
              this.msalService.instance.setActiveAccount(payload.account);
            }
            this.authService.setInteractionCompleted();
            this.loaderService.reset();
            this.sessionActivityService.touch();
            this.analytics.startSession();
            // Mobile: force navigate to chat-only view after login
            if (this.isMobileChatMode) {
              this.router.navigate(['/chat-ai']);
            }
            return;
          }
          if (msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
            this.authService.setInteractionCompleted();
            this.loaderService.reset();
            return;
          }

          if (msg.eventType === EventType.LOGOUT_SUCCESS || msg.eventType === EventType.ACCOUNT_REMOVED) {
            this.userService.clearUserInfo();
            this.userRoleService.clearUserState();
            this.clearStorageAfterLogout();
            this.router.navigate([ROUTE_PATHS.HOME]).catch(() => { /* ignore */ });
          }
        },
        error: (error) => console.error(error),
      });

    this.msalEventCallbackId = this.msalService.instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_FAILURE && event.error) {
        const error = event.error as AuthError;
        if (error.errorCode === 'access_denied' && error.errorMessage.includes('AADB2C90091')) {
          this.router.navigate([ROUTE_PATHS.HOME]);
        }
      }
    });

    this.drawerService.config$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((config: DrawerConfig | null) => {
        if (config) {
          this.currentDrawerInjector = this.createInjector(config.data);
          this.currentDrawerComponentType = config.component;
          this.currentDrawerWidth = config.width || '400px';
        } else {
          this.currentDrawerComponentType = null;
          this.currentDrawerInjector = undefined;
          this.currentDrawerWidth = '400px';
        }
        this.cdRef.markForCheck();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.msalEventCallbackId) {
      this.msalService.instance.removeEventCallback(this.msalEventCallbackId);
    }
    if (this.mobileQuery && this.mobileQueryListener) {
      this.mobileQuery.removeEventListener('change', this.mobileQueryListener);
    }
    document.body.classList.remove('mobile-chat-only');
  }

  changeCheck(newItem: boolean) {
    if (this.checkIsCollapsed === newItem) {
      return;
    }
    Promise.resolve().then(() => {
      this.checkIsCollapsed = newItem;
    });
  }
  closeDrawer(): void {
    this.drawerService.closeDrawer();
  }

  getDrawerTitle(): string {
    const data = this.currentDrawerInjector?.get(this.drawerDataToken);
    if (data?.titleKey) {
      return this.translate.instant(data.titleKey);
    }
    return data?.title || this.translate.instant('common.details');
  }

  hasCustomHeader(): boolean {
    const componentName = this.currentDrawerComponentType?.name;
    return componentName === 'EditStockDrawerComponent';
  }

  createInjector(dataToPass: any): Injector {
    return Injector.create({
      providers: [{ provide: this.drawerDataToken, useValue: dataToPass }],
      parent: this.injector
    });
  }

  private clearStorageAfterLogout(): void {
    const savedLanguage = localStorage.getItem('language');
    const accessDeniedNotice = localStorage.getItem(ACCESS_DENIED_NOTICE_KEY);
    this.localStorageService.clear();
    sessionStorage.clear();
    if (savedLanguage) {
      localStorage.setItem('language', savedLanguage);
    }
    if (accessDeniedNotice) {
      localStorage.setItem(ACCESS_DENIED_NOTICE_KEY, accessDeniedNotice);
    }
  }

  private showAccessDeniedNotice(): void {
    const notice = localStorage.getItem(ACCESS_DENIED_NOTICE_KEY);
    if (!notice) return;
    localStorage.removeItem(ACCESS_DENIED_NOTICE_KEY);
    const parsedTimestamp = Number(notice);
    if (Number.isFinite(parsedTimestamp)) {
      const maxAgeMs = 15 * 60 * 1000;
      if (Date.now() - parsedTimestamp > maxAgeMs) {
        return;
      }
    }
    this.translate
      .get(['common.accessDeniedMessage', 'common.accessDeniedTitle'])
      .pipe(take(1))
      .subscribe((translations) => {
        this.snackbarService.showError(
          translations['common.accessDeniedMessage'],
          translations['common.accessDeniedTitle'],
          'common.close',
          50000
        );
      });
  }
}
